import React, { useEffect, useState } from 'react';
import { ReactComponent as KaiziqLogoNoText } from '@Assets/kaiziqLogoNoText.svg';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import './SplashScreen.scss';

export default function SplashScreen({ fadeOut = false }) {
    const [fadeClass, setFadeClass] = useState('');
    const [splashText, setSplashText] = useState('Initializing your workspace...');
    const [progressValue, setProgressValue] = useState(0);
    const [currentStep, setCurrentStep] = useState(0);
    const className = 'splashScreen';

    const loadingSteps = [
        { text: 'Initializing your workspace...', duration: 800 },
        { text: 'Loading smart analytics...', duration: 600 },
        { text: 'Optimizing performance...', duration: 700 },
        { text: 'Preparing dashboard...', duration: 500 },
        { text: 'Almost ready!', duration: 400 }
    ];

    useEffect(() => {
        if (fadeOut) {
            setFadeClass('${className}__overlay--fadeOut');
        }
    }, [fadeOut]);

    useEffect(() => {
        let progressTimer;
        let stepTimer;
        let currentProgress = 0;
        let stepIndex = 0;

        // Smooth progress animation
        const animateProgress = () => {
            progressTimer = setInterval(() => {
                currentProgress += Math.random() * 15 + 5; // Random increment for realistic feel
                if (currentProgress > 100) currentProgress = 100;
                setProgressValue(currentProgress);
                
                if (currentProgress >= 100) {
                    clearInterval(progressTimer);
                }
            }, 100);
        };

        // Step through loading messages
        const animateSteps = () => {
            if (stepIndex < loadingSteps.length) {
                setSplashText(loadingSteps[stepIndex].text);
                setCurrentStep(stepIndex);
                
                stepTimer = setTimeout(() => {
                    stepIndex++;
                    animateSteps();
                }, loadingSteps[stepIndex].duration);
            }
        };

        animateProgress();
        animateSteps();

        return () => {
            clearInterval(progressTimer);
            clearTimeout(stepTimer);
        };
    }, []);

    return (
        <div className={className + "__overlay" + fadeClass + " width-wfa height-wfa"}>
            <div className={className + "__background"}>
                <div className={className + "__background__gradient"}></div>
                <div className={className + "__background__pattern"}></div>
                <div className={className + "__background__shapes"}>
                    <div className={className + "__background__shape --shape1"}></div>
                    <div className={className + "__background__shape --shape2"}></div>
                    <div className={className + "__background__shape --shape3"}></div>
                    <div className={className + "__background__shape --shape4"}></div>
                </div>
            </div>

            {/* Main Content */}
            <div className={className + "__container"}>
                <div className={className + "__content"}>
                    {/* Logo Section */}
                    <div className={className + "__logoSection"}>
                        <div className={className + "__logoContainer"}>
                            <div className={className + "__logoWrapper"}>
                                <KaiziqLogoNoText className={className + "__logoIcon"} />
                                <div className={className + "__logoGlow"}></div>
                            </div>
                            <div className={className + "__brandContainer"}>
                                <KaizIQText className={className + "__brandText"} />
                                <div className={className + "__tagline"}>Intelligent Productivity Platform</div>
                            </div>
                        </div>
                    </div>

                    {/* Loading Section */}
                    <div className={className + "__loadingSection"}>
                        {/* Progress Steps Indicator */}
                        <div className={className + "__stepsIndicator"}>
                            {loadingSteps.map((step, index) => (
                                <div 
                                    key={index}
                                    className={`${className}__stepDot ${
                                        index <= currentStep ? '${className}__stepDot--active' : ''
                                    } ${index < currentStep ? '${className}__stepDot--completed' : ''}`}
                                ></div>
                            ))}
                        </div>

                        {/* Loading Text */}
                        <div className={className + "__textContainer"}>
                            <div className={className + "__text"}>{splashText}</div>
                            <div className={className + "__subtext"}>
                                Setting up your personalized experience
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className={className + "__progressContainer"}>
                            <div className={className + "__progressBar"}>
                                <div 
                                    className={className + "__progressFill"}
                                    style={{ width: `${progressValue}%` }}
                                ></div>
                                <div className={className + "__progressGlow"}></div>
                            </div>
                            <div className={className + "__progressText"}>
                                {Math.round(progressValue)}%
                            </div>
                        </div>

                        {/* Feature Preview Cards */}
                        <div className={className + "__featuresPreview"}>
                            <div className={className + "__featureCard ${className}__featureCard--analytics"}>
                                <div className={className + "__featureIcon"}>📊</div>
                                <div className={className + "__featureLabel"}>Analytics</div>
                            </div>
                            <div className={className + "__featureCard ${className}__featureCard--efficiency"}>
                                <div className={className + "__featureIcon"}>⚡</div>
                                <div className={className + "__featureLabel"}>Efficiency</div>
                            </div>
                            <div className={className + "__featureCard ${className}__featureCard--goals"}>
                                <div className={className + "__featureIcon"}>🎯</div>
                                <div className={className + "__featureLabel"}>Goals</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Floating Elements */}
                <div className={className + "__floatingElements"}>
                    <div className={className + "__floatingElement --element1"}>✨</div>
                    <div className={className + "__floatingElement --element2"}>💡</div>
                    <div className={className + "__floatingElement --element3"}>🚀</div>
                    <div className={className + "__floatingElement --element4"}>⭐</div>
                </div>
            </div>

            {/* Bottom Branding */}
            <div className={className + "__footer"}>
                <div className={className + "__footerText"}>
                    Powered by AI • Designed for Teams
                </div>
            </div>
        </div>
    );
}